// Hero toolbar buttons — Receipt and Cart pills pinned to the
// pantry hero's top-right corner. Both render as 60×60 circular
// glass affordances with optional badges (dollars-this-month
// for the receipt, item count for the cart).
//
// Mobile-tap reliability — four rounds of fixes have landed:
//   1. Removed `whileHover` / `whileTap` (framer's hover state
//      eats the first tap on touch devices).
//   2. Replaced `motion.button` with plain `<button>` (framer's
//      pointer-event subscription added latency even without the
//      hover variant).
//   3. Switched the action handler from `onClick` to `onPointerUp`
//      via `useFastClick` (iOS delays synthetic clicks on
//      absolutely-positioned children of a transformed parent).
//   4. Portaled the cluster to `document.body` via
//      `<HeroToolbarCluster>` so the buttons live OUTSIDE the
//      tab-transition motion.div, the kitchen's overflow:auto
//      scroll container, and every backdrop-filter GPU layer in
//      the page tree. They mount at the document root with
//      position:fixed and become hit-testable on the very first
//      frame regardless of what the kitchen content is doing.
//
// `useFastClick(handler)` dedupes pointerup vs click so the
// handler runs exactly once, whichever event arrives first.

import { useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useTheme, THEME_TRANSITION } from "./theme";
import { font } from "./tokens";

// Fast-click hook — fires `handler` on pointerup if the pointer
// didn't drift (so a swipe / scroll doesn't accidentally trigger
// the action). Falls back to onClick for keyboard activation
// (Enter / Space synthesize a click without a pointer event) and
// for browsers where pointer events misbehave. The ref-based
// dedupe ensures handler runs exactly once per gesture even when
// both events arrive.
function useFastClick(handler) {
  const lastFireRef = useRef(0);
  const downRef = useRef(null);
  const fire = useCallback((e) => {
    const now = performance.now();
    // 350ms window covers the typical pointerup → click delay on
    // every platform we care about. If a click arrives within
    // 350ms of a pointerup that already fired, swallow it.
    if (now - lastFireRef.current < 350) return;
    lastFireRef.current = now;
    handler?.(e);
  }, [handler]);

  const onPointerDown = useCallback((e) => {
    downRef.current = { x: e.clientX, y: e.clientY };
  }, []);
  const onPointerUp = useCallback((e) => {
    const start = downRef.current;
    downRef.current = null;
    // Drift > 8px = treat as scroll / drag, not a tap. iOS
    // distinguishes touch-and-hold-then-release from genuine taps;
    // we mirror that so the user's scroll never accidentally
    // opens the receipt history.
    if (start) {
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.hypot(dx, dy) > 8) return;
    }
    fire(e);
  }, [fire]);
  const onClick = useCallback((e) => {
    // Desktop / keyboard fallback. The ref dedupe filters out the
    // synthesized click that follows pointerup.
    fire(e);
  }, [fire]);

  return { onPointerDown, onPointerUp, onClick };
}

// Press-shrink is provided via the universal `mcm-focusable` CSS
// class (defined in KitchenScreen.jsx's global style block). All
// tappable surfaces that already opt into mcm-focusable inherit a
// subtle scale(0.97) on :active. CSS-driven, no JS hop, no
// framer-motion involvement — fires on pointerdown immediately and
// composes cleanly with the useFastClick action handler since
// :active is visual-only.

// Shared style construction so both buttons match. Pulled out so a
// future third button (Camera / Notifications / etc.) can drop in
// without re-inventing the surface treatment.
//
// NO backdrop-filter — the user reported watching the glass effect
// "delay the load in" on mobile. Each backdrop-filter element creates
// its own GPU layer, and iOS Safari needs every layer to finish
// compositing before the parent element accepts taps. With 5+ blur
// layers on the kitchen (search bar, dock, tiles, two top-right
// buttons), the buttons were unresponsive for ~200-400ms during page
// load. Solid translucent fill mimics the glass aesthetic without the
// GPU-layer tax — same readable contrast, instant tap response.
function makeButtonStyle(theme) {
  return {
    position: "relative",
    width: 60, height: 60,
    borderRadius: 999,
    border: `1px solid ${theme.color.glassBorder}`,
    // Solid translucent surface (no backdrop-filter). glassFillHeavy
    // already carries enough alpha to read as a "lit pill" against
    // the warm-cream backdrop without needing a real blur underneath.
    background: theme.color.glassFillHeavy,
    boxShadow: theme.shadow.soft,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", padding: 0,
    // Mobile-tap hardening:
    //   touchAction:"manipulation"  → kills the iOS 300ms double-tap-zoom delay
    //   WebkitTapHighlightColor:"transparent" → no grey iOS tap-highlight pause
    //   userSelect:"none" / WebkitUserSelect → no long-press text selection
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
    WebkitUserSelect: "none",
    ...THEME_TRANSITION,
  };
}

// Receipt button — 60×60 glass pill in the hero top-right with the
// bundled receipt.svg. Optional `spendCents` shows up as a small
// burnt dollar-amount badge pinned to the upper-right corner.
export function ReceiptButton({ spendCents = 0, onClick }) {
  const { theme } = useTheme();
  const dollars = Math.round(spendCents / 100);
  const formatted = dollars >= 1000
    ? `$${(dollars / 1000).toFixed(1)}k`
    : `$${dollars}`;
  const label = spendCents > 0
    ? `Receipt history · ${formatted} this month`
    : "Receipt history";
  const handlers = useFastClick(onClick);
  return (
    <button
      type="button"
      {...handlers}
      aria-label={label}
      title={label}
      className="mcm-focusable"
      style={makeButtonStyle(theme)}
    >
      <img
        src="/icons/receipt.svg"
        alt="" aria-hidden
        // pointerEvents:"none" forces every tap on the icon area to
        // resolve to the parent button rather than the <img>. iOS
        // sometimes treats <img> as draggable and captures the
        // pointer, eating the click — disabling its event surface
        // makes the button the only valid hit target.
        style={{
          width: 38, height: 38, objectFit: "contain",
          pointerEvents: "none",
        }}
        draggable={false}
      />
      {spendCents > 0 && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: -6, right: -8,
            minWidth: 22, height: 22,
            padding: "0 7px",
            borderRadius: 999,
            background: theme.color.burnt,
            color: theme.color.ctaText,
            fontFamily: font.mono, fontSize: 11, fontWeight: 600,
            letterSpacing: "-0.02em",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 6px rgba(168,73,17,0.35)",
            border: `1px solid ${theme.color.glassBorder}`,
            // Same reason as the icon — badge sticks out past the
            // button bounds (top:-6, right:-8). Without
            // pointerEvents:"none" a tap on the badge becomes a
            // click on the span, never on the button.
            pointerEvents: "none",
          }}
        >
          {formatted}
        </span>
      )}
    </button>
  );
}

// Cart button — 60×60 glass pill with the bundled
// shopping_cart.svg. Burnt count badge appears when the shopping
// list has items.
export function CartButton({ count = 0, onClick }) {
  const { theme } = useTheme();
  const label = count === 0
    ? "Shopping list"
    : `Shopping list · ${count} item${count === 1 ? "" : "s"}`;
  const handlers = useFastClick(onClick);
  return (
    <button
      type="button"
      {...handlers}
      aria-label={label}
      title={label}
      className="mcm-focusable"
      style={makeButtonStyle(theme)}
    >
      <img
        src="/icons/shopping_cart.svg"
        alt="" aria-hidden
        style={{
          width: 38, height: 38, objectFit: "contain",
          pointerEvents: "none",
        }}
        draggable={false}
      />
      {count > 0 && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: -4, right: -4,
            minWidth: 22, height: 22,
            padding: "0 6px",
            borderRadius: 999,
            background: theme.color.burnt,
            color: theme.color.ctaText,
            fontFamily: font.mono, fontSize: 11, fontWeight: 600,
            letterSpacing: "-0.02em",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 2px 6px rgba(168,73,17,0.35)",
            border: `1px solid ${theme.color.glassBorder}`,
            pointerEvents: "none",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// HeroToolbarCluster — portal-rendered top-right cluster.
//
// Renders the Receipt + Cart buttons in a `position: fixed`
// container portaled to `document.body`. The buttons therefore
// live OUTSIDE every parent that could delay their availability:
//   - App.jsx's tab-transition <motion.div> with translateY
//   - KitchenScreen's <motion.div> opacity / scroll wrapper
//   - The hero's content column with stacked backdrop-filter
//     GPU layers
//
// Result: the buttons mount at the document root, paint
// independently of the kitchen's load-in animation, and are
// hit-testable from the first frame.
//
// Props mirror the inline cluster KitchenScreen used to render —
// pass the same callbacks + values and this drops in.
//
// `enabled` lets the consumer toggle the cluster off (e.g. when a
// modal is open, when the user is searching, when the demo is on a
// non-pantry tab). When false the cluster unmounts cleanly.
export function HeroToolbarCluster({
  enabled = true,
  onOpenReceipts,
  onGoToShopping,
  spendCents = 0,
  shoppingCount = 0,
}) {
  if (typeof document === "undefined") return null;
  if (!enabled) return null;
  if (!onOpenReceipts && !onGoToShopping) return null;

  return createPortal((
    <div
      style={{
        position: "fixed",
        top: "calc(22px + env(safe-area-inset-top))",
        right: 20,
        // Same z-index ladder as the FloatingLocationDock (20). The
        // tab-transition motion.div sits at the React tree's root
        // without a z-index, so anything ≥ 1 wins. We pick 20 to
        // sit above tile content (z 4) but below modal sheets (z
        // 50) and the barcode scanner (z 1000).
        zIndex: 20,
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        // Pointer-events are on the buttons themselves — the
        // wrapper just positions. No pointer surface here means
        // there's no chance the wrapper itself eats a tap during
        // any future styling.
        pointerEvents: "none",
      }}
    >
      {onOpenReceipts && (
        <div style={{ pointerEvents: "auto" }}>
          <ReceiptButton spendCents={spendCents} onClick={onOpenReceipts} />
        </div>
      )}
      {onGoToShopping && (
        <div style={{ pointerEvents: "auto" }}>
          <CartButton count={shoppingCount} onClick={onGoToShopping} />
        </div>
      )}
    </div>
  ), document.body);
}
